// Small, pure rules of the sign-in page. Guard: LoginPage.test.tsx

/**
 * Where to go after signing in — only a page of THIS app. `?redirect=` comes
 * from the address bar, so anyone can write one; a link like
 * /login?redirect=https://… or //… must never send a fresh session elsewhere.
 */
export function safeRedirect(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/')) return null;
  if (raw.startsWith('//') || /[\\\u0000-\u001f\u007f]/.test(raw)) return null;
  return raw;
}

/** The team: the only people who may sign in with Google. */
export function isTeam(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'staff';
}


/** A refusal Supabase put on the way-back address (in the #hash or the ?query). */
export function oauthErrorInUrl(loc: { hash: string; search: string }): { code: string | null; notAllowed: boolean } | null {
  for (const part of [loc.hash, loc.search]) {
    const p = new URLSearchParams(part.replace(/^[#?]/, ''));
    if (p.get('error') || p.get('error_code') || p.get('error_description')) {
      const code = p.get('error_code');
      // Our allowlist hook (migration 261) says « storiz_not_allowed »; a
      // closed sign-up says « signup_disabled ». Both mean: not on the list.
      const notAllowed = code === 'signup_disabled'
        || /storiz_not_allowed/.test(p.get('error_description') ?? '');
      return { code, notAllowed };
    }
  }
  return null;
}

// ── Why the server signed someone out (worker lib/authGate.ts, 2026-10-21) ──
const REFUSAL_KEY = 'sz-login-refused';

const REFUSALS: Record<string, string> = {
  not_on_list: 'Ce compte n’a pas accès à Storiz.',
  password_off: 'La connexion par mot de passe est désactivée. Utilisez « Continuer avec Google ».',
  service_no_google: 'Ce compte ne peut pas se connecter avec Google.',
  revoked: 'Vous avez été déconnecté de cet appareil. Reconnectez-vous.',
  expired: 'Votre session avait plus de 30 jours. Reconnectez-vous.',
};

export function rememberLoginRefusal(reason: string): void {
  try { sessionStorage.setItem(REFUSAL_KEY, reason); } catch { /* private mode */ }
}

/** The message for the last refusal, once (reading it clears it). */
export function takeLoginRefusal(): string | null {
  try {
    const r = sessionStorage.getItem(REFUSAL_KEY);
    sessionStorage.removeItem(REFUSAL_KEY);
    return r ? (REFUSALS[r] ?? null) : null;
  } catch { return null; }
}
